/**
 * One invoice, and everything that can be done to it.
 *
 * The page has two shapes, and the boundary between them is `issue`:
 *
 * * **A draft** can have time, expenses, a milestone or a hand-written line
 *   pulled onto it, and can be voided. It is working paper.
 * * **An issued invoice** is a document the client has. Nothing on it can
 *   change. Payments are recorded against it, and a mistake is corrected with a
 *   credit note — never by editing what was sent.
 *
 * That is the API's rule, and this page's job is to make it obvious *before*
 * somebody tries, rather than to discover it from a refusal.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, ChevronDown, Clock, FileMinus, Plus, Receipt, Send, Wallet } from 'lucide-react';
import {
  Alert,
  api,
  ApiError,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  DetailField,
  DetailGrid,
  DropdownMenu,
  ErrorState,
  formatDate,
  formatMoney,
  LoadingState,
  Modal,
  PageHeader,
  ScrollArea,
  StatusChip,
  Textarea,
  toast,
} from '@connect/web-shared';

import { INVOICE_STATUS_TONES, LINE_KIND_LABELS } from '../../lib/tones';
import { AddLineDialog } from './add-line-dialog';
import { RecordPaymentDialog } from './record-payment-dialog';

interface Line {
  kind: string;
  description: string;
  quantity: string;
  unit_rate: string;
  amount: string;
  taxable: boolean;
}

interface InvoiceDetail {
  public_id: string;
  code: string;
  status: string;
  client_public_id: string;
  client_name: string;
  project_name: string | null;
  account_manager_name: string | null;
  issue_date: string | null;
  due_date: string | null;
  currency: string;
  subtotal: string;
  tax_amount: string;
  total: string;
  paid_amount: string;
  balance: string;
  line_count: number;
  notes: string | null;
  internal_notes: string | null;
  lines: Line[];
  created_at: string;
}

export function InvoiceDetailPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [addingLine, setAddingLine] = useState(false);
  const [paying, setPaying] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [crediting, setCrediting] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');

  const invoice = useQuery({
    queryKey: ['finance', 'invoices', publicId],
    queryFn: () => api.get<InvoiceDetail>(`/finance/invoices/${publicId}`),
    enabled: Boolean(publicId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['finance'] });
    void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    void queryClient.invalidateQueries({ queryKey: ['expenses'] });
  };

  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.post(`/finance/invoices/${publicId}/${path}`, body),
    onSuccess: (_data, variables) => {
      invalidate();
      const said: Record<string, string> = {
        'add-time': 'Approved time added',
        'add-expenses': 'Rebillable expenses added',
        issue: 'Invoice issued',
      };
      toast.success(said[variables.path] ?? 'Done');
    },
    onError: (error) => {
      // "There is no approved, uninvoiced time on this client" is the useful
      // half of the answer, so it is shown as written.
      toast.error(
        'Could not do that',
        error instanceof ApiError ? error.message : undefined,
      );
    },
  });

  if (invoice.isLoading) return <LoadingState label="Opening the invoice" />;
  if (invoice.isError) {
    const error = invoice.error;
    return (
      <ErrorState
        title={error instanceof ApiError && error.isNotFound ? 'Invoice not found' : undefined}
        message={error instanceof ApiError ? error.message : undefined}
        requestId={error instanceof ApiError ? error.requestId : undefined}
        onRetry={() => void invoice.refetch()}
      />
    );
  }

  const row = invoice.data;
  if (!row) return null;

  const draft = row.status === 'draft';
  const issued = !draft && row.status !== 'void';
  const settled = Number(row.balance) <= 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title={row.code}
        description={`${row.client_name}${row.project_name ? ` · ${row.project_name}` : ''}`}
        breadcrumbs={[{ label: 'Invoices', to: '/invoices' }, { label: row.code }]}
        actions={
          <>
            <StatusChip value={row.status} tones={INVOICE_STATUS_TONES} />
            {draft ? (
              <Can permission="finance.invoice.edit">
                <DropdownMenu
                  label="Add to it"
                  trigger={(props) => (
                    <Button variant="secondary" size="sm" {...props}>
                      <Plus className="size-3.5" />
                      Add to it
                      <ChevronDown className="size-3.5" />
                    </Button>
                  )}
                  items={[
                    {
                      label: 'Approved time',
                      icon: <Clock className="size-3.5" />,
                      onSelect: () => act.mutate({ path: 'add-time' }),
                    },
                    {
                      label: 'Rebillable expenses',
                      icon: <Receipt className="size-3.5" />,
                      onSelect: () => act.mutate({ path: 'add-expenses' }),
                    },
                    {
                      label: 'A line by hand',
                      icon: <Plus className="size-3.5" />,
                      onSelect: () => setAddingLine(true),
                    },
                  ]}
                />
                <Button size="sm" onClick={() => setIssuing(true)} disabled={row.lines.length === 0}>
                  <Send className="size-3.5" />
                  Issue
                </Button>
              </Can>
            ) : null}
            {issued && !settled ? (
              <Can permission="finance.payment.create">
                <Button size="sm" onClick={() => setPaying(true)}>
                  <Wallet className="size-3.5" />
                  Record payment
                </Button>
              </Can>
            ) : null}
            {issued ? (
              <Can permission="finance.invoice.edit">
                <Button variant="secondary" size="sm" onClick={() => setCrediting(true)}>
                  <FileMinus className="size-3.5" />
                  Credit
                </Button>
              </Can>
            ) : null}
            {draft ? (
              <Can permission="finance.invoice.delete">
                <Button variant="ghost" size="sm" onClick={() => setVoiding(true)}>
                  <Ban className="size-3.5" />
                  Void
                </Button>
              </Can>
            ) : null}
          </>
        }
      />

      {draft ? (
        <Alert tone="info" title="This is a draft">
          The client cannot see it. Pull in approved time or rebillable expenses, add anything by
          hand, then issue it — after which nothing on it can change.
        </Alert>
      ) : null}

      {row.status === 'credited' ? (
        <Alert tone="warning" title="Credited">
          A credit note was raised against this invoice, so nothing is owed on it. Both documents
          stay on the client's account.
        </Alert>
      ) : null}

      {row.status === 'void' ? (
        <Alert tone="info" title="Void">
          This draft was cancelled before it was sent. Any time and expenses on it were released
          back to be invoiced elsewhere.
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Lines"
            description={
              row.lines.length === 0
                ? 'Nothing on it yet.'
                : `${row.lines.length} ${row.lines.length === 1 ? 'line' : 'lines'}`
            }
          />
          <CardBody className="p-0">
            <ScrollArea className="">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-2xs uppercase tracking-wide text-faint">
                    <th className="px-4 py-2 text-left font-medium">Description</th>
                    <th className="px-4 py-2 text-left font-medium">Kind</th>
                    <th className="px-4 py-2 text-right font-medium">Qty</th>
                    <th className="px-4 py-2 text-right font-medium">Rate</th>
                    <th className="px-4 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {row.lines.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted">
                        Nothing yet. Use “Add to it” above.
                      </td>
                    </tr>
                  ) : (
                    row.lines.map((line, index) => (
                      <tr key={index} className="border-b border-line/60">
                        <td className="px-4 py-2.5 text-body">
                          {line.description}
                          {!line.taxable ? (
                            <span className="ml-1.5 text-2xs text-faint">(no tax)</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 text-2xs text-faint">
                          {LINE_KIND_LABELS[line.kind] ?? line.kind}
                        </td>
                        <td className="px-4 py-2.5 text-right text-muted tabular">
                          {line.quantity}
                        </td>
                        <td className="px-4 py-2.5 text-right text-muted tabular">
                          {formatMoney(line.unit_rate, row.currency)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-body tabular">
                          {formatMoney(line.amount, row.currency)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ScrollArea>
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Totals" />
            <CardBody className="space-y-1.5 text-sm">
              <p className="flex justify-between">
                <span className="text-muted">Subtotal</span>
                <span className="tabular text-body">
                  {formatMoney(row.subtotal, row.currency)}
                </span>
              </p>
              <p className="flex justify-between">
                <span className="text-muted">Tax</span>
                <span className="tabular text-body">
                  {formatMoney(row.tax_amount, row.currency)}
                </span>
              </p>
              <p className="flex justify-between border-t border-line pt-1.5 font-semibold">
                <span className="text-heading">Total</span>
                <span className="tabular text-heading">
                  {formatMoney(row.total, row.currency)}
                </span>
              </p>
              {Number(row.paid_amount) !== 0 ? (
                <>
                  <p className="flex justify-between">
                    <span className="text-muted">Paid</span>
                    <span className="tabular text-success">
                      −{formatMoney(row.paid_amount, row.currency)}
                    </span>
                  </p>
                  <p className="flex justify-between border-t border-line pt-1.5 font-semibold">
                    <span className="text-heading">Outstanding</span>
                    <span className="tabular text-heading">
                      {formatMoney(row.balance, row.currency)}
                    </span>
                  </p>
                </>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Details" />
            <CardBody>
              <DetailGrid className="sm:grid-cols-1">
                <DetailField label="Client">
                  <button
                    type="button"
                    className="text-accent hover:underline"
                    onClick={() => navigate(`/clients/${row.client_public_id}`)}
                  >
                    {row.client_name}
                  </button>
                </DetailField>
                <DetailField label="Issued">{formatDate(row.issue_date)}</DetailField>
                <DetailField label="Due">{formatDate(row.due_date)}</DetailField>
                <DetailField label="Account manager">
                  {row.account_manager_name ?? '—'}
                </DetailField>
                <DetailField label="Currency">{row.currency}</DetailField>
              </DetailGrid>
              {row.notes ? (
                <p className="mt-3 whitespace-pre-line border-t border-line pt-3 text-xs text-muted">
                  {row.notes}
                </p>
              ) : null}
            </CardBody>
          </Card>
        </div>
      </div>

      <AddLineDialog
        open={addingLine}
        invoicePublicId={row.public_id}
        currency={row.currency}
        onClose={() => setAddingLine(false)}
        onAdded={() => {
          invalidate();
          setAddingLine(false);
        }}
      />

      <RecordPaymentDialog
        open={paying}
        invoicePublicId={row.public_id}
        invoiceCode={row.code}
        currency={row.currency}
        outstanding={row.balance}
        onClose={() => setPaying(false)}
        onRecorded={() => {
          invalidate();
          setPaying(false);
        }}
      />

      <Confirm
        open={issuing}
        title={`Issue ${row.code}?`}
        message={`${formatMoney(row.total, row.currency)} to ${row.client_name}. After this the invoice cannot be edited — a mistake is corrected with a credit note, and the client will see this one in their portal.`}
        confirmLabel="Issue it"
        variant="primary"
        onClose={() => setIssuing(false)}
        onConfirm={async () => {
          await act.mutateAsync({ path: 'issue', body: {} });
        }}
      />

      <Modal
        open={crediting}
        onClose={() => setCrediting(false)}
        title={`Credit ${row.code}`}
        description="Raises a mirrored credit note. Both documents stay on the client's account."
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setCrediting(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={!reason.trim()}
              loading={act.isPending}
              onClick={async () => {
                await act.mutateAsync({ path: 'credit', body: { reason: reason.trim() } });
                setCrediting(false);
                setReason('');
              }}
            >
              Raise credit note
            </Button>
          </>
        }
      >
        <Textarea
          label="Why"
          required
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Recorded on both documents. An unexplained credit note is a question for somebody later."
          placeholder="Billed against the wrong project."
        />
      </Modal>

      <Modal
        open={voiding}
        onClose={() => setVoiding(false)}
        title={`Void ${row.code}`}
        description="Only a draft can be voided. Its time and expenses go back to being billable."
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setVoiding(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={!reason.trim()}
              loading={act.isPending}
              onClick={async () => {
                await act.mutateAsync({ path: 'void', body: { reason: reason.trim() } });
                setVoiding(false);
                setReason('');
                navigate('/invoices');
              }}
            >
              Void it
            </Button>
          </>
        }
      >
        <Textarea
          label="Why"
          required
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Raised in error."
        />
      </Modal>
    </div>
  );
}
